import React from "react";
import { useAuth } from "../hooks/useAuth";

const LogoMark = ({ size = 72 }) => (
  <svg viewBox="0 0 680 680" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
    <rect width="680" height="680" fill="#080C12"/>
    <line x1="40" y1="112" x2="640" y2="112" stroke="#0D1825" strokeWidth="2"/>
    <line x1="40" y1="184" x2="640" y2="184" stroke="#0D1825" strokeWidth="2"/>
    <line x1="40" y1="256" x2="640" y2="256" stroke="#0D1825" strokeWidth="2"/>
    <line x1="40" y1="328" x2="640" y2="328" stroke="#0D1825" strokeWidth="2"/>
    <line x1="40" y1="400" x2="640" y2="400" stroke="#0D1825" strokeWidth="2"/>
    <line x1="40" y1="472" x2="640" y2="472" stroke="#0D1825" strokeWidth="2"/>
    <line x1="40" y1="544" x2="640" y2="544" stroke="#0D1825" strokeWidth="2"/>
    <line x1="112" y1="40" x2="112" y2="640" stroke="#0D1825" strokeWidth="2"/>
    <line x1="184" y1="40" x2="184" y2="640" stroke="#0D1825" strokeWidth="2"/>
    <line x1="256" y1="40" x2="256" y2="640" stroke="#0D1825" strokeWidth="2"/>
    <line x1="328" y1="40" x2="328" y2="640" stroke="#0D1825" strokeWidth="2"/>
    <line x1="400" y1="40" x2="400" y2="640" stroke="#0D1825" strokeWidth="2"/>
    <line x1="472" y1="40" x2="472" y2="640" stroke="#0D1825" strokeWidth="2"/>
    <line x1="544" y1="40" x2="544" y2="640" stroke="#0D1825" strokeWidth="2"/>
    <polygon points="340,80 565.2,210 565.2,470 340,600 114.8,470 114.8,210" fill="none" stroke="#C9A227" strokeWidth="22" strokeLinejoin="round"/>
    <polygon points="340,120 530.5,230 530.5,450 340,560 149.5,450 149.5,230" fill="#080C12" stroke="#2B5EA7" strokeWidth="16" strokeLinejoin="round"/>
    <line x1="149.5" y1="340" x2="254" y2="340" stroke="#2B5EA7" strokeWidth="16" strokeLinecap="butt"/>
    <line x1="426" y1="340" x2="530.5" y2="340" stroke="#2B5EA7" strokeWidth="16" strokeLinecap="butt"/>
    <line x1="340" y1="120" x2="340" y2="254" stroke="#2B5EA7" strokeWidth="16" strokeLinecap="butt"/>
    <line x1="340" y1="426" x2="340" y2="560" stroke="#2B5EA7" strokeWidth="16" strokeLinecap="butt"/>
    <circle cx="340" cy="340" r="86" fill="none" stroke="#2B5EA7" strokeWidth="16"/>
    <circle cx="340" cy="340" r="48" fill="#D93025"/>
    <polygon points="318,316 318,364 374,340" fill="#E8EBF0"/>
    <ellipse cx="340" cy="120" rx="28" ry="11" fill="#C9A227"/>
    <ellipse cx="340" cy="110" rx="18" ry="10" fill="#C9A227"/>
  </svg>
);

export default function Login() {
  const { login, authError, resetPassword } = useAuth();
  const [showReset, setShowReset] = React.useState(false);
  const [resetEmail, setResetEmail] = React.useState("");
  const [resetMsg, setResetMsg] = React.useState(null);
  const [resetLoading, setResetLoading] = React.useState(false);

  const handleReset = async () => {
    if (!resetEmail.trim()) return;
    setResetLoading(true);
    const result = await resetPassword(resetEmail.trim());
    setResetMsg(result.success
      ? "✓ Reset email sent — check your inbox."
      : "⚠ " + result.error
    );
    setResetLoading(false);
  };

  return (
    <div style={{
      minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center",
      background:"#080C12",
      backgroundImage:"linear-gradient(#0D1825 1px,transparent 1px),linear-gradient(90deg,#0D1825 1px,transparent 1px)",
      backgroundSize:"48px 48px",
      fontFamily:"Inter,system-ui,sans-serif",
      position:"relative", overflow:"hidden"
    }}>
      {/* Decorative corner lines */}
      <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:"#D93025" }}/>
      <div style={{ position:"absolute", bottom:0, left:0, right:0, height:3, background:"#C9A227" }}/>

      <div style={{
        width:400, maxWidth:"92%", padding:"40px 36px",
        background:"#0D1825",
        border:"1px solid #1A2940",
        borderTop:"3px solid #C9A227",
        borderBottom:"3px solid #2B5EA7",
        borderRadius:4,
        textAlign:"center",
        boxShadow:"0 0 60px rgba(43,94,167,0.12)",
        position:"relative"
      }}>
        {/* Gold corner accents */}
        <div style={{ position:"absolute", top:8, left:8, width:12, height:12, borderTop:"2px solid #C9A227", borderLeft:"2px solid #C9A227" }}/>
        <div style={{ position:"absolute", top:8, right:8, width:12, height:12, borderTop:"2px solid #C9A227", borderRight:"2px solid #C9A227" }}/>
        <div style={{ position:"absolute", bottom:8, left:8, width:12, height:12, borderBottom:"2px solid #C9A227", borderLeft:"2px solid #C9A227" }}/>
        <div style={{ position:"absolute", bottom:8, right:8, width:12, height:12, borderBottom:"2px solid #C9A227", borderRight:"2px solid #C9A227" }}/>

        <div style={{ marginBottom:20 }}>
          <LogoMark size={72}/>
        </div>

        <div style={{ fontFamily:"Impact,'Arial Black',sans-serif", fontSize:28, letterSpacing:"0.1em", textTransform:"uppercase", color:"#E8EBF0", lineHeight:1.1, marginBottom:4 }}>
          Creator Commandos
        </div>
        <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.24em", color:"#5A6A7E", marginBottom:28 }}>
          Manage Your Mission
        </div>

        {/* Divider */}
        <div style={{ height:1, background:"linear-gradient(90deg,transparent,#2B5EA7,transparent)", marginBottom:28, opacity:0.5 }}/>

        {authError && (
          <div style={{ background:"rgba(217,48,37,0.1)", border:"1px solid #D93025", borderRadius:3, padding:"10px 14px", marginBottom:20, fontSize:12, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:"#D93025", lineHeight:1.5 }}>
            ⚠ {authError}
          </div>
        )}

        <button onClick={login} style={{
          width:"100%", padding:"13px 20px", borderRadius:3,
          border:"1px solid #2B5EA7",
          background:"#2B5EA7",
          color:"#E8EBF0",
          fontFamily:"inherit", fontSize:11, fontWeight:700,
          textTransform:"uppercase", letterSpacing:"0.14em",
          cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:12,
          transition:"all 0.15s"
        }}
        onMouseOver={e => { e.currentTarget.style.background="#C9A227"; e.currentTarget.style.borderColor="#C9A227"; e.currentTarget.style.color="#080C12"; }}
        onMouseOut={e => { e.currentTarget.style.background="#2B5EA7"; e.currentTarget.style.borderColor="#2B5EA7"; e.currentTarget.style.color="#E8EBF0"; }}>
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign In with Google
        </button>

        {!showReset ? (
          <div style={{ marginTop:20 }}>
            <div style={{ fontSize:10, fontWeight:600, color:"#5A6A7E", textTransform:"uppercase", letterSpacing:"0.1em", lineHeight:1.8 }}>
              Sign in to manage your YouTube channel
            </div>
            <button onClick={() => setShowReset(true)} style={{ marginTop:10, background:"none", border:"none", color:"#3A4A5E", fontFamily:"inherit", fontSize:11, cursor:"pointer", textDecoration:"underline" }}>
              Forgot password?
            </button>
          </div>
        ) : (
          <div style={{ marginTop:20 }}>
            <div style={{ fontSize:11, color:"#5A6A7E", marginBottom:10 }}>Enter your email to receive a reset link:</div>
            <input
              type="email"
              value={resetEmail}
              onChange={e => setResetEmail(e.target.value)}
              placeholder="your@email.com"
              style={{ width:"100%", padding:"10px 12px", background:"#080C12", border:"1px solid #1A2940", borderRadius:3, color:"#E8EBF0", fontFamily:"inherit", fontSize:12, marginBottom:10, boxSizing:"border-box" }}
            />
            {resetMsg && <div style={{ fontSize:11, color: resetMsg.startsWith("✓") ? "#34d399" : "#D93025", marginBottom:10 }}>{resetMsg}</div>}
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={handleReset} disabled={resetLoading} style={{ flex:1, padding:"10px", background:"#2B5EA7", border:"none", borderRadius:3, color:"#E8EBF0", fontFamily:"inherit", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", cursor:"pointer" }}>
                {resetLoading ? "Sending..." : "Send reset link"}
              </button>
              <button onClick={() => { setShowReset(false); setResetMsg(null); }} style={{ padding:"10px 14px", background:"none", border:"1px solid #1A2940", borderRadius:3, color:"#5A6A7E", fontFamily:"inherit", fontSize:11, cursor:"pointer" }}>
                Back
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
